import { type FC, type ReactNode, useEffect, useRef, useState } from "react";

import { clsx } from "clsx";

import { faChevronDown } from "@fortawesome/pro-duotone-svg-icons/faChevronDown";
import { faChevronRight } from "@fortawesome/pro-duotone-svg-icons/faChevronRight";
import { faMemo } from "@fortawesome/pro-duotone-svg-icons/faMemo";
import { faNotebook } from "@fortawesome/pro-duotone-svg-icons/faNotebook";
import { faXmark } from "@fortawesome/pro-duotone-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DateTimeFormatter } from "./date_time_formatter";
import { IconButton } from "./icon_button";
import { Tooltip } from "./tooltip";

// "3 notes" at the end of the assistant's line, once there are any. "fresh":
// it saved one in the answer in progress (pulsing dot); "open": the notes are
// showing.
export type NotesState = "idle" | "fresh" | "open";

export const NotesCount: FC<{
  count: number;
  state: NotesState;
  disabled: boolean;
  // Why it's disabled, on hover.
  tip?: string;
  onClick(): void;
}> = ({ count, state, disabled, tip, onClick }) => {
  const button = (
    <button
      type="button"
      aria-pressed={state === "open"}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11.5px] whitespace-nowrap disabled:pointer-events-none",
        "font-semibold text-violet-700",
        state === "fresh" && "bg-violet-100",
        state === "open" && "bg-violet-200",
      )}
    >
      {state === "fresh" ? (
        <span className="size-1.5 animate-pulse rounded-full bg-violet-600" />
      ) : (
        <FontAwesomeIcon icon={faMemo} className="text-violet-600" />
      )}
      {`${count} ${count === 1 ? "note" : "notes"}`}
    </button>
  );

  return (
    <span className="flex shrink-0 border-l border-violet-100 pl-2">
      {tip ? (
        <Tooltip tip={tip} placement="bottom-end">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </span>
  );
};

// The notes' header, pinned: violet, so it reads as the assistant's, with
// who shares the notes and a close button.
export const NotesHeader: FC<{ onClose(): void }> = ({ onClose }) => (
  <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-100 py-1.5 pr-1.5 pl-3">
    <FontAwesomeIcon icon={faNotebook} className="text-violet-600" />
    <span className="text-[13px] font-bold text-slate-900">
      Assistant's notes
    </span>
    <span className="min-w-0 truncate text-[11px] text-slate-500">
      · Shared by every chat on this case
    </span>
    <span className="ml-auto shrink-0 rounded-md border border-violet-200">
      <IconButton
        icon={faXmark}
        aria-label="Close the notes"
        onClick={onClose}
      />
    </span>
  </div>
);

// The notes as one sheet of paper (pale amber), a rule between sections.
export const NotesSheet: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="divide-y divide-amber-200 overflow-hidden rounded-[10px] border border-amber-200 bg-amber-50 shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
    {children}
  </div>
);

// One note on the sheet: its title and when it was saved and when this chat
// read it, opened by a click to show its points. `current`: the note the
// assistant last read or wrote, banded and scrolled to.
export const NoteSection: FC<{
  title: string;
  rewritten: boolean;
  updatedAt: string;
  readAt?: string | null;
  current: boolean;
  expanded: boolean;
  onToggle(): void;
  children?: ReactNode;
}> = ({
  title,
  rewritten,
  updatedAt,
  readAt,
  current,
  expanded,
  onToggle,
  children,
}) => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (current) ref.current?.scrollIntoView({ block: "nearest" });
  }, [current]);

  return (
    <section
      ref={ref}
      className={clsx(
        "scroll-mt-28 py-2.5 pr-[18px] pl-3",
        current && "bg-amber-100 shadow-[inset_3px_0_0_#8b5cf6]",
      )}
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex w-full items-start gap-1.5 text-left"
      >
        <span className="flex h-5 w-3 shrink-0 items-center text-[11px] text-slate-500">
          <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
        </span>
        <h3 className="text-[13.5px] font-bold text-slate-800">{title}</h3>
        <span className="ml-auto flex shrink-0 flex-col items-end text-[11px] leading-4 text-slate-500">
          <span className={clsx(current && "text-violet-700")}>
            {rewritten ? "Updated" : "Saved"}{" "}
            <DateTimeFormatter iso={updatedAt} format="timeOrDate" />
          </span>
          {readAt && (
            <span>
              Read <DateTimeFormatter iso={readAt} format="timeOrDate" />
            </span>
          )}
        </span>
      </button>
      {expanded && (
        <div className="flex flex-col gap-2 pt-1.5 pl-[18px]">{children}</div>
      )}
    </section>
  );
};

// An open note with nothing in it.
export const NoteEmpty: FC = () => (
  <p className="text-[12.5px] text-slate-500">Empty</p>
);

const COLLAPSED_LINES = 5;

// A note's subhead and its points; long lists show five until opened.
export const NoteRow: FC<{ label?: string | null; lines: string[] }> = ({
  label,
  lines,
}) => {
  const [all, setAll] = useState(false);
  const shown = all ? lines : lines.slice(0, COLLAPSED_LINES);

  return (
    <div>
      {label && (
        <div className="text-[12px] font-semibold text-slate-600">{label}</div>
      )}
      <ul className="text-[12.5px] leading-[1.6] text-slate-700">
        {shown.map((line, index) => (
          <li key={index} className="flex gap-2">
            <span className="mt-[9px] size-1 shrink-0 rounded-full bg-slate-400" />
            <span className="min-w-0">{line}</span>
          </li>
        ))}
      </ul>
      {lines.length > COLLAPSED_LINES && (
        <button
          type="button"
          onClick={() => setAll((value) => !value)}
          className="mt-0.5 text-[11.5px] font-semibold text-slate-600 hover:text-slate-800"
        >
          {all ? "Show fewer" : `Show all ${lines.length}`}
        </button>
      )}
    </div>
  );
};
